import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { wellnessService } from "@/services/wellnessService";
import VoiceCheckInResultsModal from "./VoiceCheckInResultsModal";

interface VoiceRecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_DURATION_MS = 3 * 60 * 1000; // 3 minutes
const WARNING_DURATION_MS = 2.5 * 60 * 1000; // 2:30
const MIN_DURATION_MS = 5 * 1000; // 5 seconds

export default function VoiceRecordingModal({
  isOpen,
  onClose,
}: VoiceRecordingModalProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [extractedResults, setExtractedResults] = useState<any>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');
  const finalDurationRef = useRef<number>(0);
  const autoStartTriggeredRef = useRef(false);
  const { toast } = useToast();

  // Format duration as MM:SS
  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Auto-start recording when modal opens
  useEffect(() => {
    if (isOpen && !autoStartTriggeredRef.current && !isRecording && !isProcessing) {
      autoStartTriggeredRef.current = true;
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        startRecording();
      }, 300);
      return () => clearTimeout(timer);
    }
    
    // Reset ref when modal closes
    if (!isOpen) {
      autoStartTriggeredRef.current = false;
    }
  }, [isOpen, isRecording, isProcessing]);

  // Request microphone permission and start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Determine MIME type based on browser support
      let mimeType = "audio/webm";
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
        mimeType = "audio/ogg;codecs=opus";
      }
      mimeTypeRef.current = mimeType;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        
        // Stop all tracks to release microphone
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }

        // Auto-submit after recording stops
        await handleAutoSubmit(blob, finalDurationRef.current);
      };

      mediaRecorder.start();
      const startTime = Date.now();
      startTimeRef.current = startTime;
      setDuration(0);
      setShowWarning(false);
      setIsRecording(true);
    } catch (error: any) {
      console.error("Error starting recording:", error);
      toast({
        title: "Microphone access denied",
        description: "Please allow microphone access to record voice check-ins.",
        variant: "destructive",
      });
      handleClose();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Save final duration before stopping
      finalDurationRef.current = duration;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleAutoSubmit = async (blob: Blob, recordedDuration: number) => {
    // Validate minimum duration
    if (recordedDuration < MIN_DURATION_MS) {
      toast({
        title: "Recording too short",
        description: `Please record at least ${MIN_DURATION_MS / 1000} seconds. Your recording was ${(recordedDuration / 1000).toFixed(1)} seconds.`,
        variant: "destructive",
      });
      handleClose();
      return;
    }

    setIsProcessing(true);

    try {
      const result = await wellnessService.submitVoiceCheckInSync(blob, mimeTypeRef.current);

      // Store extracted results and show results modal
      setExtractedResults(result);
      setIsProcessing(false);
      handleClose();
      setShowResultsModal(true);
    } catch (error: any) {
      console.error("Error submitting voice check-in:", error);
      setIsProcessing(false);
      
      let errorMessage = "Failed to submit voice check-in. Please try again.";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : errorData.error.message || errorData.error.code || errorMessage;
        } else if (errorData.message) {
          errorMessage = typeof errorData.message === 'string'
            ? errorData.message
            : errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Submission failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      handleClose();
    }
  };

  const handleClose = () => {
    if (isRecording && mediaRecorderRef.current) {
      // Don't trigger onstop auto-submit when closing
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setDuration(0);
    setShowWarning(false);
    setIsProcessing(false);
    autoStartTriggeredRef.current = false;
    startTimeRef.current = null;
    finalDurationRef.current = 0;
    chunksRef.current = [];
    onClose();
  };

  // Timer effect - update duration when recording
  useEffect(() => {
    if (!isRecording || !startTimeRef.current) {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Start timer
    timerRef.current = window.setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Date.now() - startTimeRef.current;
        setDuration(elapsed);

        // Show warning at 2:30
        if (elapsed >= WARNING_DURATION_MS) {
          setShowWarning((prev) => {
            if (!prev) {
              toast({
                title: "Recording time limit approaching",
                description: "You have 30 seconds remaining.",
                variant: "default",
              });
              return true;
            }
            return prev;
          });
        }

        // Auto-stop at 3 minutes
        if (elapsed >= MAX_DURATION_MS) {
          if (mediaRecorderRef.current) {
            finalDurationRef.current = elapsed;
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            toast({
              title: "Recording time limit reached",
              description: "Maximum recording duration is 3 minutes.",
              variant: "default",
            });
          }
        }
      }
    }, 100);

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording, toast]);

  const handleResultsModalClose = () => {
    setShowResultsModal(false);
    setExtractedResults(null);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Voice Check-in</DialogTitle>
            <DialogDescription>
              {isRecording 
                ? "Recording... Tap stop when you're done." 
                : isProcessing 
                  ? "Processing your recording..."
                  : "Requesting microphone access... Please allow in your browser."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Recording indicator */}
            <div className="flex flex-col items-center justify-center space-y-4">
              {isRecording ? (
                <>
                  <div className="relative">
                    <div className="h-24 w-24 rounded-full bg-red-500 animate-pulse flex items-center justify-center">
                      <Mic className="h-12 w-12 text-white" />
                    </div>
                    <div className="absolute inset-0 rounded-full border-4 border-red-500 animate-ping" />
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-mono font-bold">
                      {formatDuration(duration)}
                    </p>
                    {showWarning && (
                      <p className="text-sm text-amber-600 mt-2">
                        Time limit approaching
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Min: 5 seconds | Max: 3 minutes
                    </p>
                  </div>
                </>
              ) : isProcessing ? (
                <>
                  <div className="h-24 w-24 rounded-full bg-blue-500 animate-pulse flex items-center justify-center">
                    <Loader2 className="h-12 w-12 text-white animate-spin" />
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold">Processing...</p>
                    <p className="text-sm text-muted-foreground">
                      Transcribing and analyzing your recording...
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="h-24 w-24 rounded-full bg-muted animate-pulse flex items-center justify-center">
                    <Mic className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    Requesting microphone access...
                  </p>
                </>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 justify-center">
              {isRecording && (
                <Button
                  onClick={stopRecording}
                  variant="destructive"
                  size="lg"
                  className="flex items-center gap-2"
                >
                  <Square className="h-5 w-5" />
                  Stop & Submit
                </Button>
              )}

              {isProcessing && (
                <Button disabled size="lg" className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Processing...
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Results Modal */}
      {extractedResults && (
        <VoiceCheckInResultsModal
          isOpen={showResultsModal}
          onClose={handleResultsModalClose}
          results={extractedResults}
        />
      )}
    </>
  );
}
